import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "../lib/api";

const SiteContentContext = createContext({
  media: () => "",
  text: () => "",
  saveMedia: async () => {},
  saveText: async () => {},
  ready: false,
});

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState({ media: {}, text: {} });
  const [ready, setReady] = useState(false);

  const refresh = useCallback(() => {
    return api
      .get("/site-content")
      .then((r) => setContent({ media: r.data.media || {}, text: r.data.text || {} }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh().finally(() => setReady(true));
  }, [refresh]);

  // Admin-only save. Sends the entire bag so partial updates are safe from
  // a stale local snapshot; server upserts atomically.
  const saveMedia = useCallback(async (key, url) => {
    const nextMedia = { ...content.media, [key]: url };
    await api.put("/admin/site-content", { media: nextMedia, text: content.text });
    setContent((c) => ({ ...c, media: nextMedia }));
  }, [content]);

  const saveText = useCallback(async (key, value) => {
    const nextText = { ...content.text, [key]: value };
    await api.put("/admin/site-content", { media: content.media, text: nextText });
    setContent((c) => ({ ...c, text: nextText }));
  }, [content]);

  const value = useMemo(
    () => ({
      media: (key, fallback = "") => (content.media[key] || fallback) || undefined,
      text: (key, fallback = "") => content.text[key] || fallback,
      saveMedia,
      saveText,
      refresh,
      ready,
    }),
    [content, saveMedia, saveText, refresh, ready]
  );

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
